import { Component, effect, input, signal, ViewChild } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { catchError, finalize, forkJoin, of } from "rxjs";
import { CatchErrorService, ApiService } from "src/app/services";
import {
  DefectReviewOption,
  DefectReviewStatus,
} from "src/app/models/defect-review.model";
import {
  CommentsTextEditorComponent,
  ImageAttachmentComponent,
  StatusButtonGroupComponent,
} from "..";
import { DefectPrimaryKeyId, GuidId, IDefectStatus } from "src/app/models";
import { CommentPhotoCreate } from "src/app/services/api/comments-api.service";
import { SelectModule } from "primeng/select";
import { ButtonModule } from "primeng/button";
import { DynamicDialogRef } from "primeng/dynamicdialog";
import {
  FileSelectEvent,
  FileUpload,
  FileUploadModule,
} from "primeng/fileupload";

@Component({
  selector: "app-status-defect-dialog",
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommentsTextEditorComponent,
    ImageAttachmentComponent,
    SelectModule,
    ButtonModule,
    FileUploadModule,
    StatusButtonGroupComponent,
  ],
  templateUrl: "./status-defect-dialog.component.html",
  styleUrl: "./status-defect-dialog.component.scss",
})
export class StatusDefectDialogComponent {
  @ViewChild("textEditor", { read: CommentsTextEditorComponent })
  private textEditor: CommentsTextEditorComponent | undefined;
  /** defect id to set */
  projectId = input.required<GuidId>();
  /** defect id to set */
  defectId = input.required<DefectPrimaryKeyId>();
  /** Passed in to check if the status has to change based on review outcome */
  defectStatus = input.required<IDefectStatus>();
  /** Toggle value */
  defectStatusValue = IDefectStatus.NEW;
  /** Not in use */
  photos: CommentPhotoCreate[] = [];
  /** Select options */
  readonly reviewOptions: DefectReviewOption[] = [
    { label: "Complete", id: DefectReviewStatus.COMPLETE },
    { label: "Incomplete", id: DefectReviewStatus.NOT_REVIEWED },
    { label: "Not a Defect", id: DefectReviewStatus.NOT_A_DEFECT },
  ];
  /** save is running */
  saving = signal<boolean>(false);
  /** @ignore */
  constructor(
    private readonly cx: CatchErrorService,
    private readonly api: ApiService,
    private dialogRef: DynamicDialogRef
  ) {
    // use to maintain original defect status value
    effect(() => {
      this.defectStatusValue = this.defectStatus();
    });
  }
  /**
   * Save status and comment
   */
  save(): void {
    let completedDateString = "";
    if (
      this.defectStatusValue === IDefectStatus.COMPLETE &&
      this.defectStatus() !== IDefectStatus.COMPLETE
    ) {
      completedDateString = new Date().toISOString();
    }

    const setDefectStatus$ = this.api.defects
      .setDefectStatus$(
        this.defectStatusValue,
        completedDateString,
        this.defectId(),
        this.projectId()
      )
      .pipe(
        catchError((error) => {
          this.cx.handle(error, true);
          return of(null);
        })
      );

    const createComment$ = this.api.comments
      .create$(this.textEditor?.text ?? "", this.photos, this.defectId())
      .pipe(
        catchError((error) => {
          this.cx.handle(error, true);
          return of(null);
        })
      );

    this.saving.set(true);

    forkJoin({
      status: setDefectStatus$,
      comment: createComment$,
    })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe(({ status, comment }) => {
        if (status === null || comment === null) return;
        this.dialogRef.close(this.defectStatusValue);
      });
  }
  /**
   * Runs when files are selected via the picker
   *
   * @param {_UploaderSelectEvent} event - the _UploaderSelectEvent
   * @param {FileUpload} uploaderTool - the FileUpload
   * @return {void} a Promise that resolves to void
   */
  selectCommentImages(event: FileSelectEvent, uploaderTool: FileUpload): void {
    try {
      if (this.photos.length === 3) return;

      const files: File[] = event?.currentFiles ?? [];

      if (files.length < 1) return;

      for (let i = 0; i < files.length; i++) {
        const file: File = files[i];

        // Only process image files.
        if (!file.type.match("image.*")) {
          continue;
        }

        // Closure to capture the file information.
        const reader = new FileReader();
        reader.onload = (o: ProgressEvent<FileReader>) => {
          const timestamp = new Date().getTime();
          const newPhoto: CommentPhotoCreate = {
            base64: (o.target?.result as string) ?? "",
            filename: `${timestamp}.jpeg`,
          };

          if (this.photos.length < 3) {
            this.photos.push(newPhoto);
          }

          uploaderTool.clear();
        };
        reader.onerror = (o: ProgressEvent<FileReader>) => {
          console.error(o);
          uploaderTool.clear();
        };
        reader.readAsDataURL(file);
      }
    } catch (ex) {
      console.error(ex);
    }
  }
  /** Remove a selected photo */
  deletePhoto(index: number): void {
    this.photos.splice(index, 1);
  }
  /** Cancel */
  dismiss(): void {
    this.dialogRef.close();
  }
}
